import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DollarSign, TrendingDown, TrendingUp } from "lucide-react";
import { ChartContainer } from "@/components/charts/ChartContainer";
import type { LocacoesBiSummary } from "@/services/locacoes";
import { LocacoesStatCard } from "./LocacoesStatCard";

interface Props {
  bi: LocacoesBiSummary;
  modoEquipamento?: boolean;
  /** Oculta o gráfico de rentabilidade (ex.: filtro de máquina na visão geral). */
  hideRentabilidadeChart?: boolean;
  /** Oculta os cards financeiros (quando já renderizados em outro grid). */
  hideFinanceStats?: boolean;
}

function formatBrl(v: number): string {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
}

export type LocacoesFinanceStat = {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accentClass: string;
  subtitle?: string;
};

export function buildLocacoesFinanceStats(
  bi: LocacoesBiSummary,
  modoEquipamento = false,
): LocacoesFinanceStat[] {
  if (modoEquipamento) {
    return [
      {
        title: "Faturamento",
        value: formatBrl(bi.faturamentoTotal),
        icon: <TrendingUp className="h-5 w-5" />,
        accentClass: "border-l-4 border-l-emerald-400",
        subtitle: "Receita dos contratos selecionados",
      },
      {
        title: "Contratos com receita",
        value: bi.rentabilidadePorContrato.filter((r) => r.faturamento > 0).length,
        icon: <DollarSign className="h-5 w-5" />,
        accentClass: "border-l-4 border-l-sky-400",
        subtitle: "Contratos de equipamento faturando",
      },
      {
        title: "Margem",
        value: formatBrl(bi.margem),
        icon: <DollarSign className="h-5 w-5" />,
        accentClass: bi.margem >= 0 ? "border-l-4 border-l-sky-400" : "border-l-4 border-l-amber-400",
        subtitle: bi.faturamentoTotal > 0 ? `${bi.margemPercentual}% sobre faturamento` : undefined,
      },
    ];
  }
  return [
    {
      title: "Faturamento",
      value: formatBrl(bi.faturamentoTotal),
      icon: <TrendingUp className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-emerald-400",
      subtitle: "Receita no período",
    },
    {
      title: "Gastos totais",
      value: formatBrl(bi.gastosTotal),
      icon: <TrendingDown className="h-5 w-5" />,
      accentClass: "border-l-4 border-l-rose-400",
      subtitle: `Combustível ${formatBrl(bi.gastosCombustivel)} · Manut. ${formatBrl(bi.gastosManutencao)}`,
    },
    {
      title: "Margem",
      value: formatBrl(bi.margem),
      icon: <DollarSign className="h-5 w-5" />,
      accentClass: bi.margem >= 0 ? "border-l-4 border-l-sky-400" : "border-l-4 border-l-amber-400",
      subtitle: bi.faturamentoTotal > 0 ? `${bi.margemPercentual}% sobre faturamento` : undefined,
    },
  ];
}

export function LocacoesBiPanel({
  bi,
  modoEquipamento = false,
  hideRentabilidadeChart = false,
  hideFinanceStats = false,
}: Props) {
  const financeStats = buildLocacoesFinanceStats(bi, modoEquipamento);

  const rentData = bi.rentabilidadePorContrato
    .filter((r) => r.faturamento > 0 || r.gastos > 0)
    .slice(0, 6)
    .map((r) => ({
      nome: r.clienteNome.length > 12 ? `${r.clienteNome.slice(0, 12)}…` : r.clienteNome,
      faturamento: r.faturamento,
      gastos: r.gastos,
      margem: r.margem,
    }));

  if (hideFinanceStats && hideRentabilidadeChart) return null;

  return (
    <div className="flex flex-col gap-4">
      {!hideFinanceStats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {financeStats.map((s) => (
            <LocacoesStatCard key={s.title} {...s} />
          ))}
        </div>
      )}

      {!hideRentabilidadeChart && (
        <ChartContainer
          title="Rentabilidade por contrato"
          subtitle={modoEquipamento ? "Faturamento dos contratos selecionados" : "Faturamento vs. gastos no período"}
          height={280}
          empty={rentData.length === 0}
          emptyMessage="Sem contratos com movimentação financeira no período."
        >
          <BarChart data={rentData} margin={{ top: 16, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="nome" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip
              formatter={(v: unknown) => [formatBrl(Number(v)), ""]}
              contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
            />
            <Bar dataKey="faturamento" fill="#2b8a6a" name="Faturamento" radius={[4, 4, 0, 0]} maxBarSize={32} />
            {!modoEquipamento && (
              <Bar dataKey="gastos" fill="#a82b46" name="Gastos" radius={[4, 4, 0, 0]} maxBarSize={32} />
            )}
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
}
